<script setup lang="ts">
/**
 * Product card redesign — RedisiginProductPage `1:626`
 * Default / Hovered / Opened actions · Size Default / Mobile
 * M7: dislike← left · fav/plus← right · arrows/pager opacity · 150/300ms
 * M8: add→close + Save/Style it
 *
 * FE-063 — пункт «Compare» убран: сравнения товаров в бете не будет.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import { FairIconContainer } from '@/ui/primitives/FairIconContainer'
import { useFairLikeMotion } from '@/ui/primitives/FairLikeMotion'
import { FairPaginationBar } from '@/ui/primitives/FairPaginationBar'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'
import {
  FAIR_PRODUCT_CARD_V2_DOTS_SHOWN,
  fairProductCardV2ColorOverflow,
  fairProductCardV2ShowsArrows,
  fairProductCardV2ShowsChrome,
  fairProductCardV2ShowsPagination,
  normalizeFairProductCardV2Page,
  type FairProductCardV2ColorDot,
  type FairProductCardV2PreviewState,
  type FairProductCardV2Size,
  type FairProductCardV2State,
} from '@/ui/tokens/product-card-v2.data'
import { shouldSkipFairPaginationMotion } from '@/ui/tokens/fair-pagination-bar.data'
import discountTagUrl from '@/assets/img/discount-tag.png'

interface FairProductCardV2Props {
  brand?: string
  title?: string
  price?: string
  fromLabel?: string
  images?: string[]
  imageSrc?: string
  colors?: FairProductCardV2ColorDot[]
  /** Honest colourway count from the API; drives the "+N" pill. */
  colorsTotal?: number
  size?: FairProductCardV2Size
  page?: number
  favorite?: boolean
  /**
   * T-041 — Compact: no dislike / favourite / «+» at all (quick previews like the search drawer).
   * The card is one tap target to the PDP; elsewhere actions stay as they are.
   */
  showActions?: boolean
  interactive?: boolean
  /** Force visual state in Histoire / demos */
  previewState?: FairProductCardV2PreviewState
  ariaLabel?: string
  to?: RouteLocationRaw
  priority?: boolean
  /**
   * FE-005 — no data yet (request in flight): every part of the card is a
   * skeleton. Distinct from "data is here, the photo is still downloading",
   * which the card detects itself and covers with a media skeleton only.
   */
  loading?: boolean
  /** FE-027 — BE-008 `is_available = false`: «No longer available» instead of the price, photo dimmed (`39:39766`). */
  unavailable?: boolean
  unavailableLabel?: string
  /**
   * FE-051 — у товара есть скидка: рядом с ценой встаёт значок-бирка
   * (`94:57449`, свойство компонента `Show discount`).
   *
   * Это ПРИЗНАК, а не число: макет не показывает ни старую цену, ни процент —
   * только метку «здесь скидка». Добавлять к ней что-то от себя значило бы
   * рисовать не тот компонент.
   */
  discount?: boolean
  /** FE-029 — an extra menu row («Remove from list»); the row is there only when the label is. */
  removeLabel?: string
}

const props = withDefaults(defineProps<FairProductCardV2Props>(), {
  showActions: true,
  brand: 'Saint Laurent',
  title: 'Wool Blazer',
  price: '$1,980',
  fromLabel: 'from',
  images: undefined,
  imageSrc: undefined,
  colors: () => [],
  colorsTotal: undefined,
  size: 'default',
  page: 0,
  favorite: false,
  interactive: true,
  previewState: undefined,
  ariaLabel: undefined,
  to: undefined,
  priority: false,
  loading: false,
  unavailable: false,
  unavailableLabel: 'No longer available',
  removeLabel: undefined,
  discount: false,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
  dislike: [event: MouseEvent]
  favorite: [event: MouseEvent]
  save: [event: MouseEvent]
  remove: [event: MouseEvent]
  style: [event: MouseEvent]
  'update:page': [page: number]
  'prev-slide': [event: MouseEvent]
  'next-slide': [event: MouseEvent]
}>()

const { t } = useI18n()

const rootRef = ref<HTMLElement | null>(null)
const isHovered = ref(false)
const isOpened = ref(false)

const imageList = computed(() => {
  if (props.images && props.images.length > 0) return props.images
  if (props.imageSrc) return [props.imageSrc]
  return []
})

const totalSlides = computed(() => Math.max(1, imageList.value.length))

/** Loop track: [lastClone, ...images, firstClone] when 2+ slides */
const loopTrack = computed(() => totalSlides.value > 1)

const trackImages = computed(() => {
  const list = imageList.value
  if (!loopTrack.value || list.length === 0) return list
  return [list[list.length - 1]!, ...list, list[0]!]
})

const visibleColors = computed(() => props.colors.slice(0, FAIR_PRODUCT_CARD_V2_DOTS_SHOWN))
const colorOverflow = computed(() =>
  fairProductCardV2ColorOverflow(props.colorsTotal, visibleColors.value.length),
)

/** Hide swatch row when only one colourway (PLP polish #9) */
const showColors = computed(() => props.colors.length > 1)

/**
 * Local page so gallery moves even when parent omits v-model:page
 * (Find similar / Look). Still syncs when parent does bind.
 */
const localPage = ref(
  normalizeFairProductCardV2Page(props.page, totalSlides.value),
)

const currentPage = computed(() =>
  normalizeFairProductCardV2Page(localPage.value, totalSlides.value),
)

/**
 * FE-005 — which photos have actually decoded. Keyed by src so paging back to a
 * seen slide does not flash a skeleton again, and so a cached image (which fires
 * no `load` after hydration) can be marked from `complete` on mount.
 */
const loadedSrcs = ref(new Set<string>())

const activeSrc = computed(() => imageList.value[currentPage.value] ?? '')

const showMediaSkeleton = computed(
  () => props.loading || !activeSrc.value || !loadedSrcs.value.has(activeSrc.value),
)

function markLoaded(src: string) {
  if (!src || loadedSrcs.value.has(src)) return
  const next = new Set(loadedSrcs.value)
  next.add(src)
  loadedSrcs.value = next
}

/** A broken photo must not hold the skeleton forever — uncover the empty box. */
function handleImageSettled(event: Event, src: string) {
  void event
  markLoaded(src)
}

const imgRefs = ref<HTMLImageElement[]>([])

/** Cached photos are already decoded on mount and never fire `load`. */
function syncLoadedFromDom() {
  imgRefs.value.forEach((el) => {
    if (el?.complete && el.naturalWidth > 0) markLoaded(el.getAttribute('src') ?? '')
  })
}

/** Visual index into trackImages (offset +1 when looping) */
const trackIndex = ref(0)
const slideInstant = ref(false)
let wrapSnapTimer: ReturnType<typeof setTimeout> | null = null

function clearWrapSnapTimer() {
  if (wrapSnapTimer == null) return
  clearTimeout(wrapSnapTimer)
  wrapSnapTimer = null
}

function realTrackIndex(page: number): number {
  return loopTrack.value ? page + 1 : page
}

function snapTrackToPage(page: number) {
  slideInstant.value = true
  trackIndex.value = realTrackIndex(page)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      slideInstant.value = false
    })
  })
}

function animatePageChange(prev: number, next: number) {
  if (prev === next) {
    trackIndex.value = realTrackIndex(next)
    return
  }
  clearWrapSnapTimer()
  if (!loopTrack.value) {
    trackIndex.value = next
    return
  }
  if (!shouldSkipFairPaginationMotion(prev, next, totalSlides.value)) {
    trackIndex.value = next + 1
    return
  }
  // Wrap: animate onto clone, then snap to real slide (seamless last↔first)
  const goingForward = prev === totalSlides.value - 1 && next === 0
  trackIndex.value = goingForward ? totalSlides.value + 1 : 0
  wrapSnapTimer = setTimeout(() => {
    wrapSnapTimer = null
    snapTrackToPage(next)
  }, 420)
}

watch(
  () => props.page,
  (page) => {
    const next = normalizeFairProductCardV2Page(page, totalSlides.value)
    const prev = localPage.value
    if (next === prev) return
    localPage.value = next
    animatePageChange(prev, next)
  },
)

watch(totalSlides, (nextTotal, prevTotal) => {
  clearWrapSnapTimer()
  // Prefer prop when parent drives page; keep local when uncontrolled (prop stuck at 0)
  const source = props.page !== 0 ? props.page : localPage.value
  const nextPage = normalizeFairProductCardV2Page(source, nextTotal)
  localPage.value = nextPage
  if (prevTotal != null && nextTotal !== prevTotal) {
    snapTrackToPage(nextPage)
  }
})

// Initial align (after loopTrack / images ready)
snapTrackToPage(currentPage.value)

const resolvedState = computed<FairProductCardV2State>(() => {
  if (props.previewState) return props.previewState
  if (isOpened.value) return 'opened'
  if (isHovered.value) return 'hovered'
  return 'default'
})

const showsChrome = computed(() =>
  fairProductCardV2ShowsChrome(props.size, resolvedState.value),
)

const showsArrows = computed(() =>
  fairProductCardV2ShowsArrows(
    props.size,
    resolvedState.value,
    totalSlides.value,
  ),
)

const showsPagination = computed(() =>
  fairProductCardV2ShowsPagination(
    props.size,
    resolvedState.value,
    totalSlides.value,
  ),
)

/** A skeleton (no data yet) has nothing to act on: no dislike / heart / plus — on mobile they never hide. */
const actionsVisible = computed(() => props.showActions && !props.loading)

/** T-120 — Icon button Size: Small 32 on desktop, Mobile compact 44 (glyph 16) on mobile. */
const actionSize = computed(() => (props.size === 'mobile' ? 'mobile-compact' : 'small'))

const favoriteIcon = computed(() =>
  props.favorite ? 'favorite-filled' : 'favorite-outlined',
)

/**
 * Значок скидки не рисуется поверх «No longer available»: там, где цены нет,
 * нечего и скидывать.
 */
const showDiscountTag = computed(() => props.discount && !props.unavailable)

const priceText = computed(() => {
  if (props.unavailable) return props.unavailableLabel
  const amount = props.price.trim()
  const from = props.fromLabel.trim()
  if (!from) return amount
  return `${from} ${amount}`
})

const rootClass = computed(() => ({
  'fair-product-card-v2': true,
  [`fair-product-card-v2--${props.size}`]: true,
  [`fair-product-card-v2--${resolvedState.value}`]: true,
  'fair-product-card-v2--non-interactive': !props.interactive,
  'fair-product-card-v2--unavailable': props.unavailable,
  /* FE-005 — photo not decoded yet: it stays transparent and fades in. */
  'fair-product-card-v2--media-loading': showMediaSkeleton.value,
}))

const cardAriaLabel = computed(
  () => props.ariaLabel || `${props.brand} — ${props.title}`,
)

watch(
  () => props.previewState,
  (next) => {
    if (next === 'opened') {
      isOpened.value = true
      isHovered.value = true
    } else if (next === 'hovered') {
      isOpened.value = false
      isHovered.value = true
    } else if (next === 'default') {
      isOpened.value = false
      isHovered.value = false
    }
  },
  { immediate: true },
)

function handlePointerEnter() {
  if (!props.interactive || props.previewState) return
  isHovered.value = true
}

function handlePointerLeave() {
  if (!props.interactive || props.previewState) return
  isHovered.value = false
  // Compact: keep menu until outside tap / Escape / toggle
  if (props.size === 'mobile') return
  isOpened.value = false
}

function handleCardClick(event: MouseEvent) {
  if (!props.interactive) return
  emit('click', event)
}

function stopAction(event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
}

function handleDislike(event: MouseEvent) {
  stopAction(event)
  if (!props.interactive) return
  emit('dislike', event)
}

// T-009 — Motion · Like on the heart
const { motionClass: likeMotionClass, arm: armLikeMotion } = useFairLikeMotion(() => props.favorite)

function handleFavorite(event: MouseEvent) {
  stopAction(event)
  if (!props.interactive) return
  armLikeMotion()
  emit('favorite', event)
}

function handleToggleMenu(event: MouseEvent) {
  stopAction(event)
  if (!props.interactive || props.previewState) return
  isOpened.value = !isOpened.value
  if (isOpened.value) isHovered.value = true
}

function handleSave(event: MouseEvent) {
  stopAction(event)
  if (!props.interactive) return
  emit('save', event)
  isOpened.value = false
}

function handleRemove(event: MouseEvent) {
  stopAction(event)
  if (!props.interactive) return
  emit('remove', event)
  isOpened.value = false
}

function handleStyle(event: MouseEvent) {
  stopAction(event)
  if (!props.interactive) return
  emit('style', event)
  isOpened.value = false
}

function setPage(index: number) {
  if (!props.interactive) return
  const prev = currentPage.value
  const next = normalizeFairProductCardV2Page(index, totalSlides.value)
  if (next === prev) return
  localPage.value = next
  animatePageChange(prev, next)
  emit('update:page', next)
}

function handlePrev(event: MouseEvent) {
  stopAction(event)
  if (!props.interactive || totalSlides.value <= 1) return
  const next = (currentPage.value - 1 + totalSlides.value) % totalSlides.value
  emit('prev-slide', event)
  setPage(next)
}

function handleNext(event: MouseEvent) {
  stopAction(event)
  if (!props.interactive || totalSlides.value <= 1) return
  const next = (currentPage.value + 1) % totalSlides.value
  emit('next-slide', event)
  setPage(next)
}

function handleDocPointerDown(event: PointerEvent) {
  if (!isOpened.value || props.previewState) return
  const el = rootRef.value
  if (!el) return
  if (event.target instanceof Node && el.contains(event.target)) return
  isOpened.value = false
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !isOpened.value || props.previewState) return
  isOpened.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', handleDocPointerDown)
  document.addEventListener('keydown', handleKeydown)
  syncLoadedFromDom()
})

onBeforeUnmount(() => {
  clearWrapSnapTimer()
  document.removeEventListener('pointerdown', handleDocPointerDown)
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <article
    ref="rootRef"
    :class="rootClass"
    :aria-label="cardAriaLabel"
    @pointerenter="handlePointerEnter"
    @pointerleave="handlePointerLeave"
    @click="handleCardClick"
  >
    <RouterLink
      v-if="to"
      :to="to"
      class="fair-product-card-v2__seo-link"
      :aria-label="cardAriaLabel"
      @click.stop
    />

    <div class="fair-product-card-v2__photo">
      <div class="fair-product-card-v2__media">
        <div
          class="fair-product-card-v2__slides"
          :class="{
            'fair-product-card-v2__slides--instant': slideInstant,
          }"
          :style="{
            transform: `translateX(-${trackIndex * 100}%)`,
          }"
        >
          <img
            v-for="(src, index) in trackImages"
            :key="`${src}-track-${index}`"
            ref="imgRefs"
            class="fair-product-card-v2__img"
            :src="src"
            :alt="index === trackIndex ? title : ''"
            :loading="priority || index <= 1 ? 'eager' : 'lazy'"
            :fetchpriority="priority && index === (loopTrack ? 1 : 0) ? 'high' : undefined"
            draggable="false"
            @load="handleImageSettled($event, src)"
            @error="handleImageSettled($event, src)"
          >
        </div>

        <!-- FE-005 — holds the photo box while the file downloads (or while the
             card has no data at all); the photo fades in as this fades out. -->
        <Transition name="fair-pc2-skeleton">
          <FairSkeleton
            v-if="showMediaSkeleton"
            class="fair-product-card-v2__skeleton"
            variant="media"
            fill
          />
        </Transition>
      </div>

      <!-- T-120 — Icon button Tone=Subtle; Size Small (desktop) / Mobile compact (44, glyph 16) -->
      <FairIconButton
        v-if="actionsVisible"
        class="fair-product-card-v2__action fair-product-card-v2__dislike"
        icon="dislike"
        :size="actionSize"
        tone="subtle"
        :aria-label="t('fair_product.dislike')"
        :tabindex="showsChrome ? 0 : -1"
        :interactive="interactive"
        @click="handleDislike"
      />

      <button
        v-if="size === 'default' && totalSlides > 1"
        type="button"
        class="fair-product-card-v2__action fair-product-card-v2__nav fair-product-card-v2__nav--prev"
        :aria-label="t('fair_product.prev_image')"
        :aria-hidden="!showsArrows"
        :tabindex="showsArrows ? 0 : -1"
        :disabled="!interactive"
        @click="handlePrev"
      >
        <FairIconContainer
          name="arrow-back"
          :size="16"
        />
      </button>

      <button
        v-if="size === 'default' && totalSlides > 1"
        type="button"
        class="fair-product-card-v2__action fair-product-card-v2__nav fair-product-card-v2__nav--next"
        :aria-label="t('fair_product.next_image')"
        :aria-hidden="!showsArrows"
        :tabindex="showsArrows ? 0 : -1"
        :disabled="!interactive"
        @click="handleNext"
      >
        <FairIconContainer
          name="arrow-forward"
          :size="16"
        />
      </button>

      <div
        v-if="actionsVisible"
        class="fair-product-card-v2__side"
      >
        <!-- T-120 — heart: Tone=Default on desktop (hover visible), Strong on mobile -->
        <FairIconButton
          class="fair-product-card-v2__action"
          :class="likeMotionClass"
          :icon="favoriteIcon"
          :size="actionSize"
          :tone="size === 'mobile' ? 'strong' : 'default'"
          :aria-label="t('fair_product.favorite')"
          :aria-pressed="favorite"
          :tabindex="showsChrome ? 0 : -1"
          :interactive="interactive"
          @click="handleFavorite"
        />

        <div class="fair-product-card-v2__other">
          <FairIconButton
            class="fair-product-card-v2__action fair-product-card-v2__other-toggle"
            :class="{ 'is-open': resolvedState === 'opened' }"
            :size="actionSize"
            :aria-label="t('fair_product.more_actions')"
            :aria-expanded="resolvedState === 'opened'"
            :tabindex="showsChrome ? 0 : -1"
            :interactive="interactive"
            @click="handleToggleMenu"
          >
            <span class="fair-product-card-v2__other-icon">
              <FairIconContainer
                name="plus"
                :size="16"
              />
            </span>
          </FairIconButton>

          <Transition name="fair-pc2-menu">
            <div
              v-if="resolvedState === 'opened'"
              class="fair-product-card-v2__menu"
              role="menu"
            >
              <button
                type="button"
                class="fair-product-card-v2__menu-row"
                role="menuitem"
                @click="handleSave"
              >
                <FairIconContainer
                  name="save"
                  :size="16"
                  color="currentColor"
                />
                {{ t('fair_product.save') }}
              </button>
              <button
                v-if="removeLabel"
                type="button"
                class="fair-product-card-v2__menu-row"
                role="menuitem"
                data-testid="card-remove"
                @click="handleRemove"
              >
                <FairIconContainer
                  name="delete"
                  :size="16"
                  color="currentColor"
                />
                {{ removeLabel }}
              </button>
              <button
                type="button"
                class="fair-product-card-v2__menu-row fair-product-card-v2__menu-row--ai"
                role="menuitem"
                @click="handleStyle"
              >
                <FairIconContainer
                  name="ai"
                  :size="16"
                  color="currentColor"
                />
                {{ t('fair_product.style_it') }}
              </button>
            </div>
          </Transition>
        </div>
      </div>

      <div
        v-if="totalSlides > 1 && size !== 'mobile'"
        class="fair-product-card-v2__pager"
        :aria-hidden="!showsPagination"
      >
        <FairPaginationBar
          :count="totalSlides"
          :model-value="currentPage"
          :interactive="interactive && showsPagination"
          @update:model-value="setPage"
        />
      </div>
    </div>

    <!-- FE-005 — request still in flight: the copy has nothing to show yet, so
         brand / title / price / colours are skeletons too. Once data lands only
         the photo can still be pending, and that is the media skeleton above. -->
    <div
      v-if="loading"
      class="fair-product-card-v2__content"
      aria-hidden="true"
    >
      <FairSkeleton
        class="fair-product-card-v2__brand-skeleton"
        variant="text"
        width="45%"
        :height="12"
      />
      <div class="fair-product-card-v2__row">
        <div class="fair-product-card-v2__text">
          <FairSkeleton
            variant="text"
            width="80%"
          />
          <FairSkeleton
            variant="text"
            width="35%"
          />
        </div>
        <div class="fair-product-card-v2__colors">
          <FairSkeleton
            v-for="dotIndex in FAIR_PRODUCT_CARD_V2_DOTS_SHOWN"
            :key="`dot-skeleton-${dotIndex}`"
            variant="circle"
            :width="12"
            :height="12"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="fair-product-card-v2__content"
    >
      <p class="fair-product-card-v2__brand">
        {{ brand }}
      </p>
      <div class="fair-product-card-v2__row">
        <div class="fair-product-card-v2__text">
          <p class="fair-product-card-v2__title">
            {{ title }}
          </p>
          <!-- Price `94:57447` — цена и, если есть скидка, значок-бирка `94:57449` -->
          <p class="fair-product-card-v2__price">
            <span>{{ priceText }}</span>
            <img
              v-if="showDiscountTag"
              class="fair-product-card-v2__discount"
              :src="discountTagUrl"
              alt=""
              width="20"
              height="20"
              loading="lazy"
              decoding="async"
            >
          </p>
        </div>
        <div
          v-if="showColors"
          class="fair-product-card-v2__colors"
          aria-hidden="true"
        >
          <span
            v-for="(dot, index) in visibleColors"
            :key="`${dot.color ?? 'unresolved'}-${index}`"
            class="fair-product-card-v2__dot"
            :class="{ 'fair-product-card-v2__dot--unresolved': !dot.color }"
            :style="dot.color ? { background: dot.color } : undefined"
            :title="dot.label"
          />
          <span
            v-if="colorOverflow"
            class="fair-product-card-v2__dots-more"
          >
            +{{ colorOverflow }}
          </span>
        </div>
      </div>
    </div>
  </article>
</template>
